import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { AccountContext } from "../../store/AccountContext";
import { v4FlatWinsAtBreakpointsSx } from "../../theme/v4CompatSx";
import { Field, Form } from "react-final-form";
import {
  composeValidators,
  isRequired,
  isEmail,
  isEmailRequired,
} from "../../utils/validators";
import ReactCodeInput from "react-verification-code-input";
import { useToasts } from "react-toast-notifications";
import ProfileLoading from "./ProfileLoading";

/**
 * PM5 T2 (money path, batch "myaccount-profile") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **AUTH ESCALATION CLASS.** Both login identifiers (email and mobile) are
 * edited here, the mobile behind an SMS verification code. The conversion is
 * styling-only: no handler, validator, state value, prop, condition or DOM node
 * moved.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `formContainer` and `formHeader` were
 * declared in the v4 `useStyles` object but never referenced from the JSX.
 * `classes.submit` was referenced by four `<Button>`s but **never declared** —
 * `classes.submit` was `undefined`, so React rendered no class attribute and
 * the buttons carried no rule of their own. The `className` is dropped rather
 * than invented; what those buttons DO need is the v4 default-colour restore
 * below, which came from the library, not from the file.
 */

/* ---------------------------------------------------------------------- *
 * styled() — non-MUI elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().background — a bare <div>, which has no `sx` prop.
const PageBackground = styled("div")(({ theme }) => ({
  flexGrow: 1,
  backgroundColor: theme.palette.background.default,
  padding: "10px 0 30px 0",
}));

// was: useStyles().verificationCodeContainer — `react-verification-code-input`
// is a third-party component that only accepts a `className` (no `sx` prop),
// so this is `styled()` rather than an `sx` constant (guide §1.2).
const VerificationCodeInput = styled(ReactCodeInput)({
  width: "100% !important",
  fontWeight: "600",
  "& input": {
    height: "56px !important",
    width: "15.6% !important",
    borderTop: "none",
    borderRight: "none",
    borderLeft: "none",
    borderTopLeftRadius: "0px !important",
    borderBottomLeftRadius: "0px !important",
    borderTopRightRadius: "0px !important",
    borderBottomRightRadius: "0px !important",
    borderBottom: 0,
    backgroundColor: "#eee",
    margin: "0 0.5%",
    fontSize: "2rem",
    fontWeight: 500,
    fontFamily: ["Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
    "&:focus": {
      border: "1px solid green",
      caretColor: "green",
    },
    "&:last-child": {
      borderRight: "none",
    },
  },
});

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ v4's `<Grid container spacing={2}>` was
 *   `.MuiGrid-spacing-xs-2      { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaces that with `gap` on the container and no padding on the items
 * (guide §2). Carry v4's own declarations verbatim rather than re-deriving the
 * arithmetic: omit `spacing` (v9 default 0 ⇒ `gap: 0px`), and give each item
 * the 8px padding itself. Identical by construction — and it matters here,
 * because the outer `<Container className={formBackground}>` has its own 16px
 * padding that the item padding used to sit inside.
 */
const v4GridContainerSx = { width: "calc(100% + 16px)", margin: "-8px" };
const v4GridItemSx = { padding: "8px" };

// was: useStyles().sectionTitle. The literal `@media (min-width:600px)` /
// `(min-width:960px)` become `breakpoints.up("sm"|"md")`; `themeV5` pins those
// to the v4 values (600/960), so the emitted query is identical.
const sectionTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.8rem",
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "2rem",
    lineHeight: "2.2rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "3rem",
    lineHeight: "3.2rem",
  },
});

// was: useStyles().sectionSubTitle
const sectionSubTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.4rem",
  fontWeight: "500",
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.2rem",
    lineHeight: "1.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.4rem",
    lineHeight: "1.6rem",
  },
});

// was: useStyles().formBackground
//
// PARITY RESTORE (guide §2, "A flat `sx` declaration loses to the component's
// own breakpoint rule"): `padding` has to be re-stated at `up("sm")`.
// `Container` sets `padding-left/right: 24px` there, and in v9 that @media
// block is emitted AFTER this class's flat declarations — so above 600px the
// white form panel rendered 16px top/bottom but 24px left/right instead of
// v4's flat 16px all round. In v4 the makeStyles sheet had a later JSS index
// and won at every width.
const formBackgroundSx = (theme) =>
  v4FlatWinsAtBreakpointsSx(
    theme,
    {
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(2),
    },
    ["padding"],
    ["sm"],
  );

// was: useStyles().divider
const dividerSx = (theme) => ({
  height: "2px",
  marginBottom: theme.spacing(2),
  backgroundColor: "#e8e8e8",
});

// was: useStyles().backdrop
const backdropSx = (theme) => ({
  zIndex: theme.zIndex.drawer + 1,
  color: "#fff",
});

// was: useStyles().bodyText
const bodyTextSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  marginBottom: "10px",
  lineHeight: "1.2",
});

/**
 * was: useStyles().phoneInput, handed to `<PhoneInput inputClass={...}>`.
 *
 * `react-phone-input-2` takes class-name strings (`inputClass`), not `sx`, and
 * wrapping it in a `styled()` would not help — `styled` passes `className`,
 * which that component applies to its own container, not to the `input`. So
 * the single declaration moves onto the Grid item that already wraps it, as a
 * descendant selector against the library's own class names. No DOM node is
 * added and the value is unchanged; specificity goes from (0,1,0) to (0,3,0),
 * and since both the v4 rule and this one are `!important`, the higher
 * specificity is what decides — so it still beats the vendor stylesheet's
 * `.react-tel-input .form-control { width: 300px }` exactly as before.
 */
const phoneInputItemSx = {
  padding: "8px",
  "& .react-tel-input .form-control": {
    width: "inherit !important",
  },
};

// PARITY RESTORE (guide §2, "Button default color"): v4's plain <Button>
// defaulted to `color="default"`, whose root style was
// `color: theme.palette.text.primary`. v5+ deleted the `default` colour and
// made `primary` the default, which would paint the label lime on the lime
// `typography.button` background — an invisible button. None of the four
// buttons in this file had a rule of their own (`classes.submit` was
// undefined), so this restore is the whole of their styling.
const submitSx = { color: "text.primary" };

/**
 * #773 — the profile gate.
 *
 * `AccountContext` declares `userProfile?: UserProfile` and initialises it as
 * `useState<UserProfile>()` (AccountContext.tsx:69 and :170), so it is
 * **undefined on first render** of every cold navigation to this route.
 * `EditProfileForm` reads `userProfile.phoneNumber` / `.countryCode` inside
 * `useState(…)` INITIALISERS, which run before any JSX is evaluated — so the
 * `{userProfile && …}` wrapper used by ChangeName.js:212 cannot save this file
 * and an early return cannot be placed ahead of its own hooks. Splitting the
 * gate into this parent is what makes the guard reachable: the form's hooks
 * only ever run once a profile exists.
 *
 * Optional chaining (`userProfile?.phoneNumber`) was the other candidate and is
 * REJECTED here on purpose. It stops the throw, but `useState` captures its
 * initialiser exactly once, so `phoneNumber`/`countryCode` would stay
 * `undefined` for the life of the component even after the profile arrives.
 * A customer who opens Edit Profile and presses "Update Mobile" without
 * retyping their number would then call
 * `getVerificationCode({ countryCode: undefined, phoneNumber: undefined })` —
 * trading a visible crash for a silently broken SMS verification. This split
 * keeps every read in the form below byte-identical to what shipped.
 */
export default function EditProfile() {
  const { userProfile } = useContext(AccountContext);

  if (!userProfile) {
    return <ProfileLoading />;
  }

  return <EditProfileForm />;
}

function EditProfileForm() {
  const {
    userProfile,
    update,
    updating,
    updateError,
    getVerificationCode,
    waitingOnVerificationCode,
    resetLoginWithPhone,
  } = useContext(AccountContext);
  const { addToast } = useToasts();

  const [verificationCode, setVerificationCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(userProfile.phoneNumber);
  const [countryCode, setCountryCode] = useState(userProfile.countryCode);
  const [emailNameSubmitted, setEmailNameSubmitted] = useState(false);
  const [mobileSubmitted, setMobileSubmitted] = useState(false);
  const onChange = (value, { dialCode }) => {
    setPhoneNumber(value.substring(dialCode.length));
    setCountryCode(dialCode);
  };

  useEffect(() => {
    return () => resetLoginWithPhone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitEmailName = async ({ email, firstName, lastName }) => {
    setEmailNameSubmitted(true);
    setMobileSubmitted(false);
    try {
      await update({ email, firstName, lastName });
      addToast("Profile updated successfully", { appearance: "success" });
    } catch (e) {
      addToast("Ooops! There was an error saving your profile information", {
        appearance: "error",
      });
    }
  };

  const onSubmitMobile = useCallback(async () => {
    setMobileSubmitted(true);
    setEmailNameSubmitted(false);
    try {
      const success = await getVerificationCode({ countryCode, phoneNumber });
      if (!success) {
        throw new Error();
      }

      addToast(
        "Please check your phone for the verification we have sent you",
        {
          appearance: "success",
        },
      );
    } catch (e) {
      addToast("Oops! There was an error sending the verification code", {
        appearance: "error",
      });
    }
  }, [addToast, getVerificationCode, phoneNumber, countryCode]);

  const onSubmitCode = useCallback(async () => {
    try {
      await update({
        countryCode: countryCode,
        phoneNumber: phoneNumber,
        phoneVerificationCode: verificationCode,
      });
      addToast("Profile updated successfully", {
        appearance: "success",
      });
    } catch (e) {}
  }, [addToast, update, phoneNumber, countryCode, verificationCode]);

  return (
    <PageBackground>
      <Container>
        <Grid container>
          <Grid size={{ xs: 12, sm: 12, md: 12 }}>
            <Typography variant="h1" sx={sectionTitleSx}>
              Edit Profile
            </Typography>
            <Divider sx={dividerSx} />
            <Box sx={{ my: 4 }}>
              <Typography variant="h2" sx={sectionSubTitleSx}>
                Your Details
              </Typography>
              <Container sx={formBackgroundSx}>
                <Form
                  onSubmit={onSubmitEmailName}
                  initialValues={{
                    email: userProfile.email,
                    firstName: userProfile.firstName,
                    lastName: userProfile.lastName,
                  }}
                >
                  {(props) => (
                    <form onSubmit={props.handleSubmit}>
                      <Grid container sx={v4GridContainerSx}>
                        <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                          {updateError && emailNameSubmitted && (
                            <Alert
                              severity="error"
                              style={{ marginBottom: "0.5rem" }}
                            >
                              {updateError}
                            </Alert>
                          )}
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                          <Field
                            name="email"
                            validate={composeValidators(
                              isEmailRequired,
                              isEmail,
                            )}
                          >
                            {({ input, meta }) => (
                              /* PARITY RESTORE: `variant="standard"` is v4's
                                 TextField default. v5+ made "outlined" the
                                 default, which would swap the underline input
                                 for a boxed one and route the theme's
                                 `MuiInput.underline` overrides to a component
                                 that is no longer rendered. `InputLabelProps`
                                 was removed in the v9 line;
                                 `slotProps.inputLabel` is the replacement. */
                              <TextField
                                fullWidth
                                variant="standard"
                                name="email"
                                label="Email"
                                required
                                {...input}
                                disabled={updating}
                                type="email"
                                placeholder="Email"
                                error={meta.error && meta.touched}
                                helperText={meta.error}
                                slotProps={{
                                  inputLabel: {
                                    shrink: true,
                                  },
                                }}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6 }} sx={v4GridItemSx}>
                          <Field
                            name="firstName"
                            validate={composeValidators(isRequired)}
                          >
                            {({ input, meta }) => (
                              <TextField
                                fullWidth
                                variant="standard"
                                name="firstName"
                                label="First Name"
                                required
                                {...input}
                                disabled={updating}
                                type="text"
                                placeholder="First Name"
                                error={meta.error && meta.touched}
                                helperText={meta.error}
                                slotProps={{
                                  inputLabel: {
                                    shrink: true,
                                  },
                                }}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6 }} sx={v4GridItemSx}>
                          <Field
                            name="lastName"
                            validate={composeValidators(isRequired)}
                          >
                            {({ input, meta }) => (
                              <TextField
                                fullWidth
                                variant="standard"
                                name="lastName"
                                label="Last Name"
                                required
                                {...input}
                                disabled={updating}
                                type="text"
                                placeholder="Last Name"
                                error={meta.error && meta.touched}
                                helperText={meta.error}
                                slotProps={{
                                  inputLabel: {
                                    shrink: true,
                                  },
                                }}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                          <Button
                            sx={submitSx}
                            type="submit"
                            disabled={updating}
                          >
                            Update Profile
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  )}
                </Form>
              </Container>
            </Box>
            <Box sx={{ my: 4 }}>
              <Typography variant="h2" sx={sectionSubTitleSx}>
                Your Mobile
              </Typography>
              {!waitingOnVerificationCode && (
                <Container sx={formBackgroundSx}>
                  {/* KNOWN NUMBER — the pair is only meaningful together, so
                      this stays guarded on both: the "update" form renders
                      `countryCode + phoneNumber` into the input, and a missing
                      half would print "undefined" into it. The else-branch
                      below is its exact COMPLEMENT, so no profile shape can
                      fall between them. See the dead-zone note there. */}
                  {userProfile.countryCode && userProfile.phoneNumber && (
                    <Form
                      onSubmit={onSubmitMobile}
                      initialValues={{
                        countryCode: userProfile.countryCode,
                        phoneNumber: userProfile.phoneNumber,
                      }}
                    >
                      {(props) => (
                        <form onSubmit={props.handleSubmit}>
                          <Grid container sx={v4GridContainerSx}>
                            <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                              {updateError && mobileSubmitted && (
                                <Alert severity="error">{updateError}</Alert>
                              )}
                            </Grid>
                            <Grid
                              size={{ xs: 12, sm: 12 }}
                              sx={phoneInputItemSx}
                            >
                              <PhoneInput
                                inputProps={{
                                  name: "mobileNumber",
                                  required: true,
                                  autoFocus: false,
                                }}
                                placeholder="Mobile Number"
                                aria-label="Mobile Number"
                                preferredCountries={[
                                  "gb",
                                  "ie",
                                  "us",
                                  "au",
                                  "nz",
                                ]}
                                excludeCountries={["je"]}
                                onChange={onChange}
                                disabled={updating}
                                enableTerritories={true}
                                value={
                                  userProfile.countryCode +
                                  userProfile.phoneNumber
                                }
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                              <Button
                                sx={submitSx}
                                type="submit"
                                disabled={updating}
                              >
                                Update Mobile
                              </Button>
                            </Grid>
                          </Grid>
                        </form>
                      )}
                    </Form>
                  )}
                  {/* NO NUMBER KNOWN — the exact complement of the branch
                      above, deliberately, so every profile shape lands in one
                      of the two.

                      Was `!countryCode && !phoneNumber`, which left a DEAD ZONE:
                      a profile carrying a dial code but no number (`countryCode:
                      "44", phoneNumber: null`) satisfied NEITHER branch, so
                      "Your Mobile" rendered as an empty white panel with no form
                      and no way to add a number. Observed on the deployed build
                      against the `qa-active` fixture, whose `/profile/me`
                      returns exactly that pair.

                      Operator ruling (2026-07-31): **a dial code with no number
                      is the same as no number at all — no number is known.** So
                      that profile belongs here, on the "add your mobile" form.
                      `AddPhone.js` already took this position (it branches on
                      `!userProfile.phoneNumber`); this file was the side that
                      disagreed. Written as `!(a && b)` rather than
                      `!phoneNumber` so the two branches are provably total for
                      the mirror shape too — a number with no dial code. */}
                  {!(userProfile.countryCode && userProfile.phoneNumber) && (
                    <Form onSubmit={onSubmitMobile}>
                      {(props) => (
                        <form onSubmit={props.handleSubmit}>
                          <Grid container sx={v4GridContainerSx}>
                            <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                              {updateError && mobileSubmitted && (
                                <Alert severity="error">{updateError}</Alert>
                              )}
                            </Grid>
                            <Grid
                              size={{ xs: 12, sm: 12 }}
                              sx={phoneInputItemSx}
                            >
                              <PhoneInput
                                inputProps={{
                                  name: "mobileNumber",
                                  required: true,
                                  autoFocus: false,
                                }}
                                placeholder="Mobile Number"
                                aria-label="Mobile Number"
                                country="gb"
                                preferredCountries={[
                                  "gb",
                                  "ie",
                                  "us",
                                  "au",
                                  "nz",
                                ]}
                                excludeCountries={["je"]}
                                enableTerritories={true}
                                onChange={onChange}
                                disabled={updating}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                              <Button
                                sx={submitSx}
                                type="submit"
                                disabled={updating}
                              >
                                Update Mobile
                              </Button>
                            </Grid>
                          </Grid>
                        </form>
                      )}
                    </Form>
                  )}
                </Container>
              )}

              {waitingOnVerificationCode && (
                <Grid container sx={v4GridContainerSx}>
                  <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                    <Grid container sx={v4GridContainerSx}>
                      <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                        <Typography
                          variant="body1"
                          sx={bodyTextSx}
                          align="center"
                        >
                          Let&apos;s make sure it&apos;s really you. Please
                          enter the verification code sent to your mobile
                          number.
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                        {updateError && mobileSubmitted && (
                          <Alert severity="error">{updateError}</Alert>
                        )}
                      </Grid>

                      <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                        <VerificationCodeInput
                          type="number"
                          fields={6}
                          value={verificationCode}
                          onComplete={(e) => setVerificationCode(e)}
                          disabled={updating}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                        <Button
                          size="medium"
                          sx={submitSx}
                          onClick={onSubmitCode}
                        >
                          Verify
                        </Button>
                        <Backdrop sx={backdropSx} open={updating}>
                          <CircularProgress color="primary" />
                        </Backdrop>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </PageBackground>
  );
}
